import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useSettings } from '../../context/SettingsContext';
import { ScanBarcode, Printer, Search, X, Settings, ArrowLeft, Plus, Minus, Eye, Save, RefreshCw, ChevronRight, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { PermissionGuard } from '../../components/PermissionGuard';
import ProjectSelect from '../../components/ProjectSelect';

// ── Pure JS CODE128 barcode generator — FIXED with quiet zones ────────────────
const P128 = ['11011001100','11001101100','11001100110','10010011000','10010001100','10001001100','10011001000','10011000100','10001100100','11001001000','11001000100','11000100100','10110011100','10011011100','10011001110','10111001100','10011101100','10011100110','11001110010','11001011100','11001001110','11011100100','11001110100','11101101110','11101001100','11100101100','11100100110','11101100100','11100110100','11100110010','11011011000','11011000110','11000110110','10100011000','10001011000','10001000110','10110001000','10001101000','10001100010','11010001000','11000101000','11000100010','10110111000','10110001110','10001101110','10111011000','10111000110','10001110110','11101110110','11010001110','11000101110','11011101000','11011100010','11011101110','11101011000','11101000110','11100010110','11101101000','11101100010','11100011010','11101111010','11001000010','11110001010','10100110000','10100001100','10010110000','10010000110','10000101100','10000100110','10110010000','10110000100','10011010000','10011000010','10000110100','10000110010','11000010010','11001010000','11110111010','11000010100','10001111010','10100111100','10010111100','10010011110','10111100100','10011110100','10011110010','11110100100','11110010100','11110010010','11011011110','11011110110','11110110110','10101111000','10100011110','10001011110','10111101000','10111100010','11110101000','11110100010','10111011110','10111101110','11101011110','11110101110','11010000100','11010010000','11010011100','1100011101011'];

function makeBarcodeSVG(text, barH, barW) {
  if (!text) return '';
  barH = barH || 60;
  barW = barW || 3;

  var vals = [104], check = 104;
  for (var i = 0; i < text.length; i++) {
    var c = text.charCodeAt(i) - 32;
    if (c < 0 || c > 94) c = 0;
    vals.push(c);
    check += c * (i + 1);
  }
  vals.push(check % 103);
  vals.push(106);

  var bars = vals.map(function(v) { return P128[v] || ''; }).join('');

  // QUIET ZONES — 10 modules on each side (critical for scanner!)
  var quietZone = 10 * barW;
  var rects = '', x = quietZone;

  for (var j = 0; j < bars.length; j++) {
    if (bars[j] === '1') {
      var w = 1;
      while (j + w < bars.length && bars[j + w] === '1') w++;
      rects += '<rect x="' + x + '" y="0" width="' + (w * barW) + '" height="' + barH + '" fill="#000"/>';
      x += w * barW;  // ← FIX: x full bar width se advance hoga
      j += w - 1;
    } else {
      x += barW;      // ← FIX: space ke liye sirf 1 module
    }
  }

  var W = bars.length * barW + quietZone * 2;
  var totalH = barH + 16;

  return '<svg xmlns="http://www.w3.org/2000/svg" width="' + W + '" height="' + totalH + '" viewBox="0 0 ' + W + ' ' + totalH + '" style="display:block;max-width:100%">' +
    '<rect width="' + W + '" height="' + totalH + '" fill="white"/>' +
    rects +
    '<text x="' + Math.round(W / 2) + '" y="' + (barH + 13) + '" text-anchor="middle" font-family="monospace" font-size="11" fill="#000">' + text + '</text>' +
    '</svg>';
}

const P128_STR = JSON.stringify(P128);
const CODE128_FN_STR = `var _P=${P128_STR};function _bc(text,barH,barW){if(!text)return'';barH=barH||60;barW=barW||3;var vals=[104],check=104;for(var i=0;i<text.length;i++){var c=text.charCodeAt(i)-32;if(c<0||c>94)c=0;vals.push(c);check+=c*(i+1);}vals.push(check%103);vals.push(106);var bars=vals.map(function(v){return _P[v]||'';}).join('');var qz=10*barW;var rects='',x=qz;for(var j=0;j<bars.length;j++){if(bars[j]==='1'){var w=1;while(j+w<bars.length&&bars[j+w]==='1')w++;rects+='<rect x="'+x+'" y="0" width="'+(w*barW)+'" height="'+barH+'" fill="#000"/>';x+=w*barW;j+=w-1;}else{x+=barW;}}var W=bars.length*barW+qz*2;var tH=barH+16;return'<svg xmlns="http://www.w3.org/2000/svg" width="'+W+'" height="'+tH+'" viewBox="0 0 '+W+' '+tH+'" style="display:block;max-width:100%"><rect width="'+W+'" height="'+tH+'" fill="white"/>'+rects+'<text x="'+Math.round(W/2)+'" y="'+(barH+13)+'" text-anchor="middle" font-family="monospace" font-size="11" fill="#000">'+text+'</text></svg>';}`;

// ── Available fields ──────────────────────────────────────────────────────────
const AVAILABLE_FIELDS = [
  { key: 'company_name',  label: 'Company Name',  default: true  },
  { key: 'item_name',     label: 'Item Name',      default: true  },
  { key: 'proto_code',    label: 'Proto Code',     default: true  },
  { key: 'dimensions',    label: 'Dimensions',     default: false },
  { key: 'material',      label: 'Material',       default: false },
  { key: 'unit_price',    label: 'MRP / Price',    default: true  },
  { key: 'quantity',      label: 'Quantity',       default: false },
  { key: 'project_name',  label: 'Project Name',   default: false },
  { key: 'description',   label: 'Description',    default: false },
  { key: 'barcode',       label: 'Barcode Image',  default: true  },
];

const PAPER_PRESETS = [
  { name: '2×1 inch',   w: 50.8, h: 25.4 },
  { name: '2×1.5 inch', w: 50.8, h: 38.1 },
  { name: '2×2 inch',   w: 50.8, h: 50.8 },
  { name: '3×2 inch',   w: 76.2, h: 50.8 },
  { name: '4×3 inch',   w: 101.6, h: 76.2 },
  { name: '4×6 inch',   w: 101.6, h: 152.4 },
  { name: 'Custom',     w: null, h: null },
];

const DEFAULT_CFG = {
  paperPreset: '2×1.5 inch',
  widthMm: 50.8,
  heightMm: 38.1,
  cols: 2,
  rows: 1,
  marginTopMm: 0,
  marginLeftMm: 1,
  marginRightMm: 0,
  marginBottomMm: 0,
  gapColMm: 0,
  gapRowMm: 0,
  barcodeHeightPx: 55,
  barcodeBarWidth: 3,      // ← FIXED: 3px minimum for reliable scanning
  barcodeWidthPct: 95,
  fontSize: 9,
  copies: 1,
  fields: AVAILABLE_FIELDS.filter(f => f.default).map(f => ({
    key: f.key,
    label: f.label,
    fontSize: f.key === 'company_name' ? 8 : f.key === 'barcode' ? 0 : 9,
    bold: f.key === 'company_name' || f.key === 'item_name',
    show: true,
  })),
};

// ── Single Label Preview ──────────────────────────────────────────────────────
function LabelPreview({ item, cfg, companyName, scale = 1 }) {
  const PX_PER_MM = 3.7795;
  const W = cfg.widthMm * PX_PER_MM;
  const H = cfg.heightMm * PX_PER_MM;
  const padL = cfg.marginLeftMm * PX_PER_MM;
  const padT = cfg.marginTopMm * PX_PER_MM;
  const padR = cfg.marginRightMm * PX_PER_MM;
  const padB = cfg.marginBottomMm * PX_PER_MM;

  const getValue = (key) => {
    if (key === 'company_name') return companyName;
    if (key === 'unit_price') return item.unit_price != null ? 'MRP : ' + parseFloat(item.unit_price).toFixed(2) : '';
    if (key === 'quantity') return item.quantity ? item.quantity + ' ' + (item.unit || '') : '';
    if (key === 'project_name') return item.project_name || '';
    return item[key] || '';
  };

  return (
    <div style={{
      width: W * scale, height: H * scale, border: '1.5px solid #555',
      background: '#fff', overflow: 'hidden', flexShrink: 0,
      transform: scale !== 1 ? `scale(${scale})` : undefined,
      transformOrigin: scale !== 1 ? 'top left' : undefined,
      fontFamily: 'Arial, sans-serif',
    }}>
      <div style={{
        padding: `${padT * scale}px ${padR * scale}px ${padB * scale}px ${padL * scale}px`,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100%', gap: 1,
      }}>
        {cfg.fields.filter(f => f.show).map(f => {
          if (f.key === 'barcode') {
            const svg = makeBarcodeSVG(item.proto_code, cfg.barcodeHeightPx * scale, Math.max(1, cfg.barcodeBarWidth * scale));
            return (
              <div key={f.key} style={{ width: cfg.barcodeWidthPct + '%' }}
                dangerouslySetInnerHTML={{ __html: svg }} />
            );
          }
          const val = getValue(f.key);
          if (!val) return null;
          return (
            <div key={f.key} style={{
              fontSize: (f.fontSize || cfg.fontSize) * scale,
              fontWeight: f.bold ? 'bold' : 'normal',
              textAlign: 'center', width: '100%',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{val}</div>
          );
        })}
      </div>
    </div>
  );
}

// ── Number input helper ───────────────────────────────────────────────────────
function NumInput({ label, value, onChange, min, max, step = 1, unit = '' }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-gray-600 flex-1">{label}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => onChange(Math.max(min, parseFloat((value - step).toFixed(2))))}
          className="w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded flex items-center justify-center text-gray-600 transition-colors">
          <Minus className="w-3 h-3" />
        </button>
        <input type="number" value={value} min={min} max={max} step={step}
          onChange={e => onChange(parseFloat(e.target.value) || min)}
          className="w-14 text-center border border-gray-200 rounded py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-amber-500" />
        <button onClick={() => onChange(Math.min(max, parseFloat((value + step).toFixed(2))))}
          className="w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded flex items-center justify-center text-gray-600 transition-colors">
          <Plus className="w-3 h-3" />
        </button>
        {unit && <span className="text-xs text-gray-400 w-6">{unit}</span>}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function BarcodePage() {
  const { settings } = useSettings();
  const { user, hasPermission } = useAuth();
  // For workers: check barcodes permission. For admin: always allow.
  const isWorker = user?.role === 'worker';
  const workerHasAccess = !isWorker || hasPermission('barcodes', 'read');
  const [allItems, setAllItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [selected, setSelected] = useState([]);
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [step, setStep] = useState(1);
  const [activeFieldIdx, setActiveFieldIdx] = useState(null);
  const [showFieldAdder, setShowFieldAdder] = useState(false);
  const companyName = settings.company_name || 'MOZI INNOVATORS LLP';

  useEffect(() => {
    Promise.all([
      api.get('/barcodes/items'),
      api.get('/projects').catch(() => ({ data: [] }))
    ]).then(([bRes, pRes]) => {
      setAllItems(bRes.data);
      setProjects(pRes.data.filter(p => p.status !== 'deleted'));
    }).catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = allItems.filter(item => {
    const ms = !search ||
      (item.proto_code || '').toLowerCase().includes(search.toLowerCase()) ||
      item.item_name.toLowerCase().includes(search.toLowerCase()) ||
      (item.project_name || '').toLowerCase().includes(search.toLowerCase());
    const mp = !projectFilter || String(item.project_id) === projectFilter;
    return ms && mp;
  });

  const selectedIds = new Set(selected.map(i => i.id));
  const toggleItem = (item) =>
    setSelected(prev => selectedIds.has(item.id) ? prev.filter(i => i.id !== item.id) : [...prev, item]);
  const removeSelected = (id) => setSelected(prev => prev.filter(i => i.id !== id));

  const setField = (idx, key, val) =>
    setCfg(c => { const fields = [...c.fields]; fields[idx] = { ...fields[idx], [key]: val }; return { ...c, fields }; });
  const removeField = (idx) =>
    setCfg(c => ({ ...c, fields: c.fields.filter((_, i) => i !== idx) }));
  const addField = (fieldKey) => {
    const meta = AVAILABLE_FIELDS.find(f => f.key === fieldKey);
    if (!meta) return;
    if (cfg.fields.find(f => f.key === fieldKey)) { toast('Field already added'); return; }
    setCfg(c => ({ ...c, fields: [...c.fields, { key: fieldKey, label: meta.label, fontSize: fieldKey === 'barcode' ? 0 : cfg.fontSize, bold: false, show: true }] }));
    setShowFieldAdder(false);
  };
  const moveField = (idx, dir) => {
    const fields = [...cfg.fields];
    const swap = idx + dir;
    if (swap < 0 || swap >= fields.length) return;
    [fields[idx], fields[swap]] = [fields[swap], fields[idx]];
    setCfg(c => ({ ...c, fields }));
    setActiveFieldIdx(swap);
  };
  const applyPreset = (name) => {
    const p = PAPER_PRESETS.find(x => x.name === name);
    if (!p || !p.w) { setCfg(c => ({ ...c, paperPreset: name })); return; }
    setCfg(c => ({ ...c, paperPreset: name, widthMm: p.w, heightMm: p.h }));
  };

  // ── Print ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!selected.length) { toast.error('Select at least one item'); return; }
    const copies = Math.max(1, cfg.copies);
    const wMm = cfg.widthMm, hMm = cfg.heightMm;
    const padL = cfg.marginLeftMm, padT = cfg.marginTopMm;
    const padR = cfg.marginRightMm, padB = cfg.marginBottomMm;

    let labelsHtml = '';
    const allPrintItems = [];
    selected.forEach(item => {
      for (let c = 0; c < copies; c++) allPrintItems.push({ ...item, _c: c });
    });

    allPrintItems.forEach((item, idx) => {
      let inner = '';
      cfg.fields.filter(f => f.show).forEach(f => {
        const fs = f.fontSize || cfg.fontSize;
        const fw = f.bold ? 'bold' : 'normal';
        if (f.key === 'barcode') {
          inner += `<div class="f-bc" id="bc_${idx}"></div>`;
        } else if (f.key === 'company_name') {
          inner += `<div style="font-size:${fs}px;font-weight:${fw};text-align:center;width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${companyName}</div>`;
        } else if (f.key === 'unit_price') {
          const v = item.unit_price != null ? 'MRP : ' + parseFloat(item.unit_price).toFixed(2) : '';
          if (v) inner += `<div style="font-size:${fs}px;font-weight:${fw};text-align:center;width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${v}</div>`;
        } else if (f.key === 'quantity') {
          const v = item.quantity ? item.quantity + ' ' + (item.unit || '') : '';
          if (v) inner += `<div style="font-size:${fs}px;font-weight:${fw};text-align:center;width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${v}</div>`;
        } else {
          const v = item[f.key] || '';
          if (v) inner += `<div style="font-size:${fs}px;font-weight:${fw};text-align:center;width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${v}</div>`;
        }
      });
      labelsHtml += `<div class="sticker">${inner}</div>`;
    });

    const itemsForScript = JSON.stringify(allPrintItems.map((it, idx) => ({ idx, proto_code: it.proto_code || '' })));

    const html = `<!DOCTYPE html><html><head><title>Labels</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:Arial,sans-serif;background:#fff}
  .grid{display:flex;flex-wrap:wrap;gap:${cfg.gapRowMm}mm ${cfg.gapColMm}mm;padding:0}
  .sticker{
    width:${wMm}mm;height:${hMm}mm;
    border:0.5px solid #aaa;
    padding:${padT}mm ${padR}mm ${padB}mm ${padL}mm;
    display:flex;flex-direction:column;
    justify-content:center;align-items:center;gap:1px;
    overflow:hidden;page-break-inside:avoid;
  }
  .f-bc{width:${cfg.barcodeWidthPct}%;margin:0 auto}
  @media print{
    @page{size:${wMm * cfg.cols + cfg.gapColMm * (cfg.cols - 1)}mm ${hMm * cfg.rows + cfg.gapRowMm * (cfg.rows - 1)}mm;margin:0}
    body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  }
</style></head><body>
<script>${CODE128_FN_STR}<\/script>
<div class="grid">${labelsHtml}</div>
<script>
var items=${itemsForScript};
var barH=${cfg.barcodeHeightPx};
var barW=${cfg.barcodeBarWidth};
items.forEach(function(it){
  var el=document.getElementById('bc_'+it.idx);
  if(el&&it.proto_code)el.innerHTML=_bc(it.proto_code,barH,barW);
});
<\/script>
</body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); }, 600);
  };

  const previewItem = selected[0] || allItems[0] || {
    item_name: 'Sample Item', proto_code: 'ITEM001',
    unit_price: 0, quantity: 1, unit: 'pcs',
    project_name: 'Sample Project', dimensions: '100x200', material: 'Wood',
    description: 'Description here',
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
    </div>
  );

  // ── STEP 1 ───────────────────────────────────────────────────────────────────
  if (!workerHasAccess) return (
    <PermissionGuard hasAccess={false} canWrite={false}><div /></PermissionGuard>
  );

  if (step === 1) return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ScanBarcode className="w-6 h-6 text-amber-600" /> Barcode Label Designer
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Step 1 — Select items to print labels for</p>
        </div>
        <button onClick={() => { if (!selected.length) { toast.error('Select at least one item first'); return; } setStep(2); }}
          disabled={!selected.length}
          className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors shadow-sm">
          Next: Design Label <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by proto code, item name, project..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"><X className="w-4 h-4" /></button>}
        </div>
        <ProjectSelect projects={projects} value={projectFilter} onChange={v => setProjectFilter(v)}
              placeholder="All Projects" format="name-code" className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white" />
      </div>

      {selected.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <p className="text-xs font-semibold text-amber-700 mb-2">{selected.length} item{selected.length > 1 ? 's' : ''} selected</p>
          <div className="flex flex-wrap gap-2">
            {selected.map(item => (
              <span key={item.id} className="flex items-center gap-1.5 bg-white border border-amber-300 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-full">
                <span className="font-mono">{item.proto_code}</span>
                <span className="text-amber-400">·</span>
                <span className="max-w-24 truncate">{item.item_name}</span>
                <button onClick={() => removeSelected(item.id)} className="text-amber-400 hover:text-amber-700 ml-0.5"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <ScanBarcode className="w-12 h-12 mx-auto mb-3 text-gray-200" />
          <p className="font-semibold text-gray-400">{search || projectFilter ? 'No items match your search' : 'No items with proto code found'}</p>
          {!search && !projectFilter && <p className="text-sm text-gray-300 mt-1">Add proto codes to project items first</p>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filtered.map(item => {
            const isSel = selectedIds.has(item.id);
            return (
              <button key={item.id} onClick={() => toggleItem(item)}
                className={`text-left rounded-2xl border-2 p-3 transition-all ${isSel ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white hover:border-amber-300'}`}>
                <div className={`w-5 h-5 rounded-full border-2 mb-2 flex items-center justify-center flex-shrink-0 ${isSel ? 'bg-amber-500 border-amber-500' : 'border-gray-300'}`}>
                  {isSel && <span className="text-white text-xs font-bold">✓</span>}
                </div>
                <p className="font-bold text-gray-900 text-xs leading-tight truncate">{item.item_name}</p>
                <p className="text-xs text-gray-400 truncate mt-0.5">{item.project_name}</p>
                <div className="mt-2 bg-amber-100 border border-amber-300 rounded px-2 py-0.5 inline-block">
                  <span className="font-mono text-xs font-bold text-amber-800">{item.proto_code}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── STEP 2 ───────────────────────────────────────────────────────────────────
  const previewScale = Math.min(1, 220 / (cfg.widthMm * 3.7795));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setStep(1)} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <ScanBarcode className="w-6 h-6 text-amber-600" /> Label Designer
            </h1>
            <p className="text-sm text-gray-500">Step 2 — Design your label layout & print</p>
          </div>
        </div>
        <button onClick={handlePrint}
          className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-2.5 rounded-xl font-semibold text-sm transition-colors shadow-sm">
          <Printer className="w-4 h-4" />
          Print {selected.length * cfg.copies} Label{selected.length * cfg.copies !== 1 ? 's' : ''}
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        {/* LEFT: Settings */}
        <div className="xl:col-span-4 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 space-y-3">
            <h3 className="font-bold text-gray-800 text-sm flex items-center gap-2">
              <Settings className="w-4 h-4 text-amber-500" /> Page / Label Size
            </h3>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Paper Preset</label>
              <div className="grid grid-cols-3 gap-1">
                {PAPER_PRESETS.map(p => (
                  <button key={p.name} onClick={() => applyPreset(p.name)}
                    className={`text-xs py-1.5 px-2 rounded-lg border transition-all ${cfg.paperPreset === p.name ? 'bg-amber-500 text-white border-amber-500' : 'border-gray-200 text-gray-600 hover:border-amber-300'}`}>
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Width (mm)</label>
                <input type="number" value={cfg.widthMm} min={10} max={300}
                  onChange={e => setCfg(c => ({ ...c, widthMm: parseFloat(e.target.value) || 50, paperPreset: 'Custom' }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-amber-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Height (mm)</label>
                <input type="number" value={cfg.heightMm} min={10} max={300}
                  onChange={e => setCfg(c => ({ ...c, heightMm: parseFloat(e.target.value) || 38, paperPreset: 'Custom' }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-amber-500" />
              </div>
            </div>
            <p className="text-xs text-gray-400 text-center">= {(cfg.widthMm / 25.4).toFixed(2)}" × {(cfg.heightMm / 25.4).toFixed(2)}"</p>
            <div className="grid grid-cols-2 gap-3">
              <NumInput label="Columns" value={cfg.cols} min={1} max={10} onChange={v => setCfg(c => ({ ...c, cols: v }))} />
              <NumInput label="Rows" value={cfg.rows} min={1} max={20} onChange={v => setCfg(c => ({ ...c, rows: v }))} />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5">Margins (mm)</p>
              <div className="space-y-1.5">
                <NumInput label="Top" value={cfg.marginTopMm} min={0} max={20} step={0.5} onChange={v => setCfg(c => ({ ...c, marginTopMm: v }))} unit="mm" />
                <NumInput label="Left" value={cfg.marginLeftMm} min={0} max={20} step={0.5} onChange={v => setCfg(c => ({ ...c, marginLeftMm: v }))} unit="mm" />
                <NumInput label="Right" value={cfg.marginRightMm} min={0} max={20} step={0.5} onChange={v => setCfg(c => ({ ...c, marginRightMm: v }))} unit="mm" />
                <NumInput label="Bottom" value={cfg.marginBottomMm} min={0} max={20} step={0.5} onChange={v => setCfg(c => ({ ...c, marginBottomMm: v }))} unit="mm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumInput label="Col gap" value={cfg.gapColMm} min={0} max={20} step={0.5} onChange={v => setCfg(c => ({ ...c, gapColMm: v }))} unit="mm" />
              <NumInput label="Row gap" value={cfg.gapRowMm} min={0} max={20} step={0.5} onChange={v => setCfg(c => ({ ...c, gapRowMm: v }))} unit="mm" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5">Barcode Size</p>
              <div className="space-y-1.5">
                <NumInput label="Bar height" value={cfg.barcodeHeightPx} min={20} max={120} step={2} onChange={v => setCfg(c => ({ ...c, barcodeHeightPx: v }))} unit="px" />
                <NumInput label="Bar module width" value={cfg.barcodeBarWidth} min={2} max={5} step={1} onChange={v => setCfg(c => ({ ...c, barcodeBarWidth: v }))} unit="px" />
                <NumInput label="Barcode width" value={cfg.barcodeWidthPct} min={30} max={100} step={5} onChange={v => setCfg(c => ({ ...c, barcodeWidthPct: v }))} unit="%" />
              </div>
            </div>
            <NumInput label="Copies per item" value={cfg.copies} min={1} max={50} step={1} onChange={v => setCfg(c => ({ ...c, copies: v }))} />
            <button onClick={() => setCfg(DEFAULT_CFG)}
              className="w-full text-xs text-gray-400 hover:text-gray-600 py-1.5 border border-dashed border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
              Reset to defaults
            </button>
          </div>

          {/* Field Designer */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-800 text-sm">Label Fields</h3>
              <button onClick={() => setShowFieldAdder(v => !v)}
                className="flex items-center gap-1 text-xs text-amber-600 hover:bg-amber-50 px-2 py-1 rounded-lg border border-amber-200 transition-colors">
                <Plus className="w-3 h-3" /> Add Field
              </button>
            </div>
            {showFieldAdder && (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                {AVAILABLE_FIELDS.filter(af => !cfg.fields.find(f => f.key === af.key)).map(af => (
                  <button key={af.key} onClick={() => addField(af.key)}
                    className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-amber-50 border-b border-gray-100 last:border-0 transition-colors">
                    + {af.label}
                  </button>
                ))}
                {AVAILABLE_FIELDS.every(af => cfg.fields.find(f => f.key === af.key)) && (
                  <p className="px-3 py-2 text-xs text-gray-400">All fields already added</p>
                )}
              </div>
            )}
            <div className="space-y-1">
              {cfg.fields.map((f, idx) => (
                <div key={f.key} onClick={() => setActiveFieldIdx(activeFieldIdx === idx ? null : idx)}
                  className={`rounded-xl border p-2.5 cursor-pointer transition-all ${activeFieldIdx === idx ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <button onClick={e => { e.stopPropagation(); setField(idx, 'show', !f.show); }}
                        className={`w-8 h-4 rounded-full transition-colors flex-shrink-0 ${f.show ? 'bg-amber-500' : 'bg-gray-200'}`}>
                        <span className={`block w-3.5 h-3.5 bg-white rounded-full shadow transition-transform mt-0.5 ${f.show ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                      <span className={`text-xs font-semibold truncate ${f.show ? 'text-gray-800' : 'text-gray-400'}`}>{f.label}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={e => { e.stopPropagation(); moveField(idx, -1); }} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded">▲</button>
                      <button onClick={e => { e.stopPropagation(); moveField(idx, 1); }} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded">▼</button>
                      <button onClick={e => { e.stopPropagation(); removeField(idx); setActiveFieldIdx(null); }}
                        className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  {activeFieldIdx === idx && f.key !== 'barcode' && (
                    <div className="mt-2 pt-2 border-t border-amber-200 flex items-center gap-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">Size:</span>
                        <button onClick={() => setField(idx, 'fontSize', Math.max(6, f.fontSize - 1))} className="w-5 h-5 bg-gray-100 hover:bg-gray-200 rounded flex items-center justify-center"><Minus className="w-2.5 h-2.5" /></button>
                        <span className="text-xs font-mono w-6 text-center">{f.fontSize}</span>
                        <button onClick={() => setField(idx, 'fontSize', Math.min(20, f.fontSize + 1))} className="w-5 h-5 bg-gray-100 hover:bg-gray-200 rounded flex items-center justify-center"><Plus className="w-2.5 h-2.5" /></button>
                        <span className="text-xs text-gray-400">px</span>
                      </div>
                      <button onClick={() => setField(idx, 'bold', !f.bold)}
                        className={`text-xs px-2 py-0.5 rounded font-bold border transition-colors ${f.bold ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-200 text-gray-500'}`}>B</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Preview */}
        <div className="xl:col-span-8 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <h3 className="font-bold text-gray-800 text-sm mb-4 flex items-center gap-2">
              <Eye className="w-4 h-4 text-amber-500" /> Live Preview — {cfg.widthMm}mm × {cfg.heightMm}mm
            </h3>
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-shrink-0" style={{ width: cfg.widthMm * 3.7795 * previewScale, height: cfg.heightMm * 3.7795 * previewScale }}>
                <LabelPreview item={previewItem} cfg={cfg} companyName={companyName} scale={previewScale} />
              </div>
              <div className="flex-1 min-w-32">
                <p className="text-xs text-gray-500 mb-2">Previewing: <b>{previewItem.item_name}</b></p>
                <p className="text-xs text-gray-400">Proto: <span className="font-mono font-bold text-amber-700">{previewItem.proto_code}</span></p>
                <div className="mt-3 space-y-1">
                  <p className="text-xs text-gray-400">• {cfg.cols} col × {cfg.rows} row per page</p>
                  <p className="text-xs text-gray-400">• {selected.length} items × {cfg.copies} copies</p>
                  <p className="text-xs font-semibold text-gray-700">= {selected.length * cfg.copies} total labels</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 text-sm">Selected Items ({selected.length})</h3>
              <button onClick={() => setStep(1)} className="text-xs text-amber-600 hover:text-amber-700 font-medium">+ Add more</button>
            </div>
            <div className="space-y-3">
              {selected.map(item => (
                <div key={item.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 border border-gray-100">
                  <div className="flex-shrink-0 overflow-hidden rounded"
                    style={{ width: Math.min(100, cfg.widthMm * 3.7795 * 0.5), height: Math.min(70, cfg.heightMm * 3.7795 * 0.5) }}>
                    <LabelPreview item={item} cfg={cfg} companyName={companyName} scale={Math.min(0.5, 100 / (cfg.widthMm * 3.7795))} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 text-sm truncate">{item.item_name}</p>
                    <p className="text-xs text-gray-400 truncate">{item.project_name}</p>
                    <p className="text-xs font-mono text-amber-700 font-bold">{item.proto_code}</p>
                  </div>
                  <button onClick={() => { removeSelected(item.id); if (selected.length === 1) setStep(1); }}
                    className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button onClick={handlePrint}
            className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white py-3.5 rounded-2xl font-bold text-sm transition-colors shadow-sm">
            <Printer className="w-5 h-5" />
            Print {selected.length * cfg.copies} Label{selected.length * cfg.copies !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
